import { buildAhaResult, buildSimulation } from '@fruitful/money-map-simulator';
import { useMemo } from 'react';

import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import {
    buildStaticRuleSetDisplays,
    computeRuleSetDisplays,
} from '../utils/simulationDisplayUtils';

const BILLS_THRESHOLD = 0.8;
const ADJUSTED_BILLS_RATIO = 0.8;
const ADJUSTED_SPEND_RATIO = 0.15;
const ADJUSTED_GOALS_RATIO = 0.05;

export const adjustMoneyMapForHighBills = (moneyMap: {
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
}) => {
    const {
        incomeTotalCents,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    } = moneyMap;

    if (
        incomeTotalCents > 0 &&
        billsTotalCents >= incomeTotalCents * BILLS_THRESHOLD
    ) {
        return {
            incomeTotalCents,
            billsTotalCents: Math.round(
                incomeTotalCents * ADJUSTED_BILLS_RATIO,
            ),
            spendTotalCents: Math.round(
                incomeTotalCents * ADJUSTED_SPEND_RATIO,
            ),
            goalsTotalCents: Math.round(
                incomeTotalCents * ADJUSTED_GOALS_RATIO,
            ),
        };
    }

    return {
        incomeTotalCents,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    };
};

export const useGetRuleSets = (
    adjustForHighBills = false,
    spendOverrideCents?: number,
) => {
    const { results } = useConversationalMMContext();
    const { moneyMap, rawChatResults } = results;

    const wasAdjusted = useMemo(
        () =>
            adjustForHighBills &&
            moneyMap.incomeTotalCents > 0 &&
            moneyMap.billsTotalCents >=
                moneyMap.incomeTotalCents * BILLS_THRESHOLD,
        [moneyMap, adjustForHighBills],
    );

    const adjusted = useMemo(
        () =>
            adjustForHighBills
                ? adjustMoneyMapForHighBills(moneyMap)
                : moneyMap,
        [moneyMap, adjustForHighBills],
    );
    const remaining = adjusted.incomeTotalCents - adjusted.billsTotalCents;
    const spendTotalCents = spendOverrideCents ?? adjusted.spendTotalCents;
    const goalsTotalCents =
        spendOverrideCents != null
            ? remaining - spendOverrideCents
            : adjusted.goalsTotalCents;

    const noGoalsMoney = useMemo(() => goalsTotalCents <= 0, [goalsTotalCents]);

    const { ruleSetDisplays, hideDates, emergencyFundUnreachable } =
        useMemo(() => {
            const simulation = buildSimulation({
                ...rawChatResults,
                monthlySpendCents: spendTotalCents,
                monthlyGoalsSpendCents: goalsTotalCents,
            });

            const displayResult =
                simulation != null && goalsTotalCents > 0
                    ? computeRuleSetDisplays(simulation)
                    : {
                          ruleSets: buildStaticRuleSetDisplays(rawChatResults),
                          exceedsDateThreshold: true,
                          emergencyFundUnreachable: true,
                          noGoalsMoney: true,
                      };
            const { ruleSets, exceedsDateThreshold } = displayResult;

            return {
                ruleSetDisplays: ruleSets.length > 0 ? ruleSets : null,
                hideDates: noGoalsMoney || exceedsDateThreshold,
                emergencyFundUnreachable:
                    displayResult.emergencyFundUnreachable,
            };
        }, [rawChatResults, spendTotalCents, goalsTotalCents, noGoalsMoney]);

    const adjustedAhaResult = useMemo(() => {
        if (!wasAdjusted) return null;
        return buildAhaResult({ ...results, moneyMap: adjusted });
    }, [wasAdjusted, results, adjusted]);

    return {
        ruleSetDisplays,
        hideDates,
        emergencyFundUnreachable,
        adjustedMoneyMap: adjusted,
        wasAdjusted,
        adjustedAhaResult,
    };
};
